import { SettingsOutlined } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext.tsx';
import { updateUserProfile } from '../services/api.ts';

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

const SettingsDialog: React.FC<SettingsDialogProps> = ({ open, onClose }) => {
  const { user, refreshUser } = useUser();
  const [autoSync, setAutoSync] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const isGoogleConnected = !!user?.google_id;

  // Initialize auto-sync state from user data
  useEffect(() => {
    if (user) {
      setAutoSync(user.auto_sync_calendar || false);
    }
  }, [user]);

  // Clear errors when dialog opens or connection status changes
  useEffect(() => {
    if (open) {
      setSaveError(null);
      setSaveSuccess(false);
    }
  }, [open, isGoogleConnected]);

  const handleAutoSyncChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    
    // Check if user has Google Calendar connected
    if (newValue && !isGoogleConnected) {
      setSaveError('Please connect your Google Calendar first to enable auto-sync');
      return;
    }
    
    setAutoSync(newValue);
    setSaveError(null);
    setSaveSuccess(false);
    setIsSaving(true);

    try {
      await updateUserProfile({ auto_sync_calendar: newValue });
      await refreshUser();
      setSaveSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error updating auto-sync setting:', error);
      setSaveError('Failed to update setting. Please try again.');
      // Revert the switch
      setAutoSync(!newValue);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SettingsOutlined />
          <Typography variant="h6">Settings</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box py={2}>
          {/* Calendar Auto-Sync Section */}
          <Box mb={3}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Calendar Integration
            </Typography>
            
            <Box sx={{ 
              opacity: isGoogleConnected ? 1 : 0.6,
              transition: 'opacity 0.2s'
            }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoSync}
                    onChange={handleAutoSyncChange}
                    disabled={isSaving || !isGoogleConnected}
                    {...(isSaving && {
                      icon: <CircularProgress size={20} />,
                      checkedIcon: <CircularProgress size={20} />
                    })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Auto-sync to Google Calendar
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Automatically sync new birthdays to your Google Calendar
                    </Typography>
                  </Box>
                }
              />
            </Box>
            
            {!isGoogleConnected && !saveError && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Connect your Google Calendar to enable auto-sync. You can connect by creating a birthday and checking the "Sync with Calendar" option.
              </Alert>
            )}
            
            {saveError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {saveError}
              </Alert>
            )}
            
            {saveSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Settings saved successfully!
              </Alert>
            )}
            
            {isGoogleConnected && !saveError && !saveSuccess && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                ✓ Google Calendar connected
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsDialog;

